import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { WidgetStore } from '../../../../../stores/grid/widget.state';
import { IndicatorDataStore } from '../../../../../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../../../../../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../../../../../stores/data-layer-type/data-layer-type.state';
import { IndicatorStore } from '../../../../../stores/indicator/indicator.state';
import { ChartData } from 'chart.js';
import { WidgetHelperService } from '../../../../../services/widget/widget-helper.service';
import { getState } from '@ngrx/signals';
import { CustomGridStackWidget } from '../../../../../services/grid-widget-config.service';
import { TableModule } from 'primeng/table';
import { NgForOf } from '@angular/common';
import { GlobalsStore } from '../../../../../stores/globals.state';
import { ExtendedChartData } from '../../../../../models/chartjs-extensions';
import { WegweiserStore } from '../../../../../stores/wegweiser-api/wegweiser.state';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [TableModule, NgForOf],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements OnInit {
  protected readonly widgetStore = inject(WidgetStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly globalsStore = inject(GlobalsStore);
  readonly wwStore = inject(WegweiserStore);
  private resizeObserver!: ResizeObserver;

  chartData!: ExtendedChartData;

  @Input() id!: string;

  initialized = false;
  options: any;
  indicatorData: any;

  tableColumns: Array<{ field: string; header: string }> = [];
  tableData: Array<{ [key: string]: any }> = [];
  additionalHeaderRow: Array<{ [key: string]: any }> = [];
  private transposed = true;
  protected config: CustomGridStackWidget | null = null;

  constructor(private widgetHelper: WidgetHelperService) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      this.init();
    });
  }

  /**
   * Initializes the widget with its configuration, options, and data.
   * Sets up the table, retrieves the chart data, and determines if the data is transposed.
   *
   * @return {void} This method does not return a value.
   */
  protected init(): void {
    if (!this.id) return;

    this.config = this.widgetStore.get(this.id);

    this.options = this.widgetHelper.getBaseOptions(
      'table',
      this.config?.widget
    );

    this.chartData = this.widgetStore.getChartData(this.id);
    this.transposed = this.config?.widget?.table?.transpose || false;

    this.setupTable();
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * It is commonly used to perform component initialization logic.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.init();
  }

  /**
   * Configures and populates the table structure with columns and data based on
   * the chartData and configuration settings. Supports both transposed and non-transposed
   * views of the data.
   *
   * In the transposed mode, labels become columns, and datasets become rows. In non-transposed
   * mode, datasets become columns, while labels determine each row.
   *
   * It also handles additional customization, such as:
   * - Replacing specific headers with localized captions.
   * - Adding an additional header row based on the displayKeys setting.
   *
   * @return {void} This method does not return a value but updates the internal state
   *                variables such as tableColumns, tableData, and additionalHeaderRow.
   */
  private setupTable(): void {
    if (!this.chartData || !this.chartData.labels) return;
    const isYearTable = this.config?.widget?.groupProperty === 'dataLayer';
    const groupHeader = isYearTable
      ? 'Jahr'
      : this.config?.widget.table?.captionDistrict || 'Gebiet';

    if (this.transposed) {
      // Transposed logic
      const labels: string[] = this.chartData.labels as string[];
      const keys: string[] = this.chartData.groups?.map(
        group => group.key
      ) as string[];
      this.tableColumns = [
        {
          field: 'label',
          header: groupHeader,
        },
        ...labels.map((label: string, index: number) => {
          const key = keys[index];
          let header = label;
          if (key === 'avg') {
            header = '#' + this.config?.widget.table?.captionAverage || label;
          }

          if (key === 'total') {
            header = '#' + this.config?.widget.table?.captionTotal || label;
          }

          return {
            field: `col${index}`,
            header: header,
          };
        }),
      ];

      this.tableData = this.chartData.datasets.map(
        (dataset: any, dataSetIdx: number) => {
          const key = this.chartData?.groups?.[dataSetIdx]?.key;
          const row: { [key: string]: any } = {
            key: key || '',
            label: dataset
              ? dataset.group && this.config?.widget?.showFullIndicatorName
                ? `${dataset.group}: ${dataset.label}`
                : dataset.label
              : '',
          };

          labels.forEach((_: string, index: number) => {
            row[`col${index}`] = this.getValue(dataset, index, dataSetIdx);
          });

          return row;
        }
      );

      if (!isYearTable && this.config?.widget?.table?.displayKeys) {
        this.additionalHeaderRow = [
          {
            field: 'label',
            header: this.config?.widget.table?.captionKey || '',
          },
          ...keys.map((key: string) => ({ field: '', header: key })), // Add keys as headers
        ];
      } else {
        this.additionalHeaderRow = []; // No extra row if displayKeys is false
      }
    } else {
      this.additionalHeaderRow = [];

      // Generate columns
      this.tableColumns = [
        {
          field: 'label',
          header: groupHeader,
        }, // First column is always labels
        ...this.chartData.datasets.map((dataset: any, idx: number) => ({
          field: dataset?.labelKey || '',
          header: dataset
            ? dataset.group && this.config?.widget?.showFullIndicatorName
              ? `${dataset.group}: ${dataset.label}`
              : dataset.label
            : '',
        })),
      ];

      if (!isYearTable && this.config?.widget?.table?.displayKeys) {
        this.tableColumns.unshift(
          {
            field: 'key',
            header: this.config?.widget.table?.captionKey || 'Schlüssel',
          }, // First column is always labels
          ...this.chartData.datasets.map((dataset: any, idx: number) => ({
            field: this.chartData?.groups?.[idx]?.key || '',
            header: dataset?.key || '',
          }))
        );
      }

      if (!this.chartData.labels) return;
      // Generate rows
      this.tableData = this.chartData.labels.map((label, index) => {
        const distKey = this.chartData?.groups?.[index].key;
        if (distKey === 'avg') {
          label = this.config?.widget.table?.captionAverage || label;
        }
        if (distKey === 'total') {
          label = this.config?.widget.table?.captionTotal || label;
        }
        const row: { [key: string]: any } = {
          label, // Always include the label
          key: this.chartData?.groups?.[index].key || '', // Add the key
        };

        this.chartData.datasets.forEach((dataset: any, dataSetIdx: number) => {
          if (!dataset.labelKey) return;
          row[dataset.labelKey] = this.getValue(
            dataset,
            distKey ? dataset.dataKeys.indexOf(distKey) : index,
            dataSetIdx
          );
        });
        return row;
      });
    }
  }

  /**
   * Retrieves a formatted value or raw data based on the configuration of the widget.
   *
   * @param {any} dataset - The dataset object containing data.
   * @param {number} index - The index of the data point within the dataset.
   * @param {number} dataSetIdx - The index of the indicator used for formatting the value.
   * @return {any} The formatted value or raw data from the dataset based on the widget configuration.
   */
  getValue(dataset: any, index: number, dataSetIdx: number): any {
    if (!this.config) return;

    if (this.config?.widget.context === 'external') {
      const indicatorId = this.config.widget.indicators[dataSetIdx];

      return this.globalsStore.getFormattedValue(
        dataset.data[index],
        this.config.widget.indicators[dataSetIdx],
        true,
        this.wwStore.getWegweiserIndicatorUnitById(indicatorId),
        'short',
        true
      );
    } else if (this.config?.widget.focusProperty === 'indicator') {
      return this.globalsStore.getFormattedValue(
        dataset.data[index],
        this.config.widget.indicator,
        true
      );
    } else if (this.config?.widget) {
      return this.globalsStore.getFormattedValue(
        dataset.data[index],
        this.config.widget.indicators[dataSetIdx],
        true
      );
    } else {
      return dataset.data[index];
    }
  }
}
